import './app.css';
import React from 'react';
import {createRoot} from 'react-dom/client';
import {CommonProvider} from '@common/core/common-provider';
import * as Sentry from '@sentry/react';
import {Playlist} from '@app/web-player/playlists/playlist';
import {Track, TRACK_MODEL} from '@app/web-player/tracks/track';
import {ALBUM_MODEL} from '@app/web-player/albums/album';
import {Artist, ARTIST_MODEL} from '@app/web-player/artists/artist';
import {Repost} from '@app/web-player/reposts/repost';
import {UserProfile} from '@app/web-player/user-profile/user-profile';
import {UserLink} from '@app/web-player/user-profile/user-link';
import {UserArtist} from '@app/web-player/user-profile/user-artist';
import {LandingPageContent} from '@app/landing-page/landing-page-content';
import {ignoredSentryErrors} from '@common/errors/ignored-sentry-errors';
import {FetchCustomPageResponse} from '@common/custom-page/use-custom-page';
import {createAppRouter} from '@app/app-router';
import {UseArtistResponse} from '@app/web-player/artists/requests/use-artist';
import {GetAlbumResponse} from '@app/web-player/albums/requests/use-album';
import {getTrackResponse} from '@app/web-player/tracks/requests/use-track';
import {Product} from '@common/billing/product';
import {GetPlaylistResponse} from '@app/web-player/playlists/requests/use-playlist';
import {GetUserProfileResponse} from '@app/web-player/user-profile/requests/use-user-profile';
import {SearchResponse} from '@app/web-player/search/requests/use-search-results';
import {BaseBackendBootstrapData} from '@common/core/base-backend-bootstrap-data';
import {BaseBackendSettings} from '@common/core/settings/base-backend-settings';
import {BaseBackendUser} from '@common/auth/base-backend-user';
import {
  getOrFetchBootstrapData,
  useBootstrapDataStore,
} from '@ui/bootstrap-data/bootstrap-data-store';
import {rootEl} from '@ui/root-el';
import {Omit} from 'utility-types';

import {AppStatusBar} from '@common/core/capacitor/app-status-bar';
import {AppNavigationBar} from '@common/core/capacitor/app-navigation-bar';
import {AppSplashScreen} from '@common/core/capacitor/app-splash-screen';

import {GetLiveEventResponse} from './admin/live-events/requests/use-live-event';
import {GetLiveEventTicketResponse} from './admin/live-events/requests/use-live-event-ticket';
import {GetLiveEventScanSessionResponse} from './admin/live-events/requests/use-live-event-scan-session';
import {GetLiveEventOrderResponse} from './web-player/live-events/requests/use-live-event-order';
import {GENRE_MODEL} from './web-player/genres/genre';
import {
  initAppPlatformMode,
  isMobileApp,
  isRenderedAsAndroid,
  isRenderedAsIOS,
} from '@common/core/platform-detection';
import {getTokenSync, initBearerToken} from '@common/auth/bearer-token';
import {GetLiveVoteResponse} from './admin/live-votes/requests/use-live-vote';
import {GetUserLiveVotesResponse} from './web-player/live-votes/requests/use-user-live-votes';

declare module '@common/http/value-lists' {
  interface FetchValueListsResponse {
    genres: {value: string; name: string}[];
    artists: {value: string; name: string}[];
  }
}

declare module '@ui/settings/settings' {
  interface Settings
    extends Omit<BaseBackendSettings, 'uploads' | 'notifications'> {
    spotify_is_setup?: boolean;
    lastfm_is_setup?: boolean;
    artist_provider?: string;
    album_provider?: string;
    search_provider?: string;
    artist_bio_provider?: string;
    wikipedia_language?: string;
    player?: {
      show_upload_btn?: boolean;
      default_volume?: number;
      log_time: number;
      log_volume_min: number;
      hide_video_button?: boolean;
      hide_radio_button?: boolean;
      track_comments?: boolean;
      sort_method?: string;
      seekbar_type?: 'waveform' | 'bar';
      enable_repost?: boolean;
      hide_queue?: boolean;
      hide_video?: boolean;
      hide_lyrics?: boolean;
      lyrics_automate?: boolean;
      enable_download?: boolean;
      show_become_artist_btn?: boolean;
      default_artist_view?: 'list' | 'grid';
      request_first_review?: number;
      request_other_review?: number;
      mobile?: {
        auto_open_overlay?: boolean;
        auto_open_overlay_video?: boolean;
        auto_open_overlay_audio?: boolean;
      };
    };
    notifications: BaseBackendSettings['notifications'] & {
      channels?: {
        showActivationPanel?: boolean;
        itemsBeforeActivationPanel?: number;
      };
    };
    uploads: BaseBackendSettings['uploads'] & {
      autoMatch?: boolean;
    };
    artistPage: {
      tabs: {id: string; active: boolean}[];
      showDescription?: boolean;
    };
    youtube?: {
      suggested_quality?: string;
      search_method?: string;
    };
    homepage: {
      type: string;
      value?: number | string;
      pricing?: boolean;
      appearance: LandingPageContent;
      trending?: boolean;
    };
    homepageApp: {
      type: string;
      value?: number | string;
      pricing?: boolean;
      appearance: LandingPageContent;
      trending?: boolean;
    };
    ads?: {
      general_top?: string;
      general_bottom?: string;
      artist_top?: string;
      artist_bottom?: string;
      album_above?: string;
      disable?: boolean;
      audio?: {
        zone?: number;
        playTimeRef?: number;
      };
    };
  }
}

declare module '@ui/types/user' {
  interface User extends BaseBackendUser {
    username?: string;
    uploaded_tracks: Track[];
    playlists: Playlist[];
    reposts?: Repost[];
    profile?: UserProfile;
    links?: UserLink[];
    artists?: UserArtist[];
  }
}

declare module '@ui/bootstrap-data/bootstrap-data' {
  interface BootstrapData extends BaseBackendBootstrapData {
    loaders?: {
      artist?: UseArtistResponse;
      artistPage?: UseArtistResponse;
      editArtistPage?: UseArtistResponse;
      album?: GetAlbumResponse;
      albumEmbed?: GetAlbumResponse;
      albumPage?: GetAlbumResponse;
      editAlbumPage?: GetAlbumResponse;
      track?: getTrackResponse;
      trackPage?: getTrackResponse;
      editTrackPage?: getTrackResponse;
      playlistPage?: GetPlaylistResponse;
      playlist?: GetPlaylistResponse;
      userProfilePage?: GetUserProfileResponse;
      searchPage?: SearchResponse;
      search?: SearchResponse;
      customPage?: FetchCustomPageResponse;
      liveEvents?: GetLiveEventResponse;
      liveEventPage?: GetLiveEventResponse;
      editLiveEventPage?: GetLiveEventResponse;
      liveEventTickets?: GetLiveEventTicketResponse;
      liveEventTicketPage?: GetLiveEventTicketResponse;
      editLiveEventTicketPage?: GetLiveEventTicketResponse;
      liveEventScanSessionPage?: GetLiveEventScanSessionResponse;
      editLiveEventScanSessionPage?: GetLiveEventScanSessionResponse;
      liveEventOrderPage?: GetLiveEventOrderResponse;
      editLiveEventOrderPage?: GetLiveEventOrderResponse;
      liveEventOrders?: GetLiveEventOrderResponse;
      liveEventTicket?: GetLiveEventOrderResponse;
      liveEventScanSessions?: GetLiveEventScanSessionResponse;
      liveVotes?: GetLiveVoteResponse;
      liveVotePage?: GetLiveVoteResponse;
      editLiveVotePage?: GetLiveVoteResponse;
      liveVotesList?: GetUserLiveVotesResponse;
      userLiveVotes?: GetUserLiveVotesResponse;
      landingPage?: {
        products: Product[];
        trendingArtists: Artist[];
      };
    };
    playlists?: Playlist[];
    artists: {
      id: number;
      name: string;
      image_small?: string;
      role: string;
    }[];
    likes?: {
      [TRACK_MODEL]: Record<number, boolean>;
      [ALBUM_MODEL]: Record<number, boolean>;
      [ARTIST_MODEL]: Record<number, boolean>;
      [GENRE_MODEL]: Record<number, boolean>;
    };
    reposts?: {
      [TRACK_MODEL]: Record<number, boolean>;
      [ALBUM_MODEL]: Record<number, boolean>;
    };
  }
}

window.addEventListener('error', e =>
  console.log('[onerror]', e.message, e.error),
);
window.addEventListener('unhandledrejection', e =>
  console.log('[unhandled]', e.reason),
);

const rendered = async () => {
  document.body.classList.remove(
    'mobile-app',
    'rendered-platform-ios',
    'rendered-platform-android',
  );

  if (isMobileApp()) {
    document.body.classList.add('mobile-app');

    if (isRenderedAsIOS()) {
      document.body.classList.add('rendered-platform-ios');
    } else if (isRenderedAsAndroid()) {
      document.body.classList.add('rendered-platform-android');
    }
  }
  AppSplashScreen.Hide();
};

const bootstrapApp = async () => {
  await initAppPlatformMode();

  await AppStatusBar.Initialize({
    overlay: true,
  });

  await AppNavigationBar.Initialize();

  await initBearerToken();
  if (isMobileApp() && getTokenSync()) {
    await useBootstrapDataStore.getState().fetchData();
  }

  const appRouter = await createAppRouter();
  const data = await getOrFetchBootstrapData();
  const sentryDsn = data?.settings?.logging?.sentry_public;

  // Configure Sentry si nécessaire
  if (sentryDsn && !import.meta.env.VITE_APP_DEV) {
    Sentry.init({
      dsn: sentryDsn,
      integrations: [new Sentry.BrowserTracing()],
      tracesSampleRate: 0.2,
      ignoreErrors: ignoredSentryErrors,
      release: data.sentry_release,
    });
  }

  // Monte l'application React
  createRoot(rootEl).render(
    <CommonProvider router={appRouter} onRenderComplete={rendered} />,
  );
};

// Lance le bootstrap de l'application
bootstrapApp().catch(err => {
  console.error("Erreur lors du bootstrap de l'application :", err);
});
